import { SelectProps } from '@amzn/awsui-components-react/polaris/select/interfaces'
import { convertToLocalTime, generateId } from '../../common/Util'
import {
    DESCRIPTION_LENGTH_LIMIT,
    PROGRAM_ID_SEPARATOR,
    PROGRAM_SELECT_VALIDATION,
    PROJECT_NAME_VALIDATION,
    ProjectType,
    SERVICE_SELECTION_VALIDATION,
    STATUS_INDICATOR_TYPES,
} from '../../Constant'
import TextPopover from '../reusable/TextPopover'
import { StatusIndicator } from '@amzn/awsui-components-react'
import DatePopover from '../reusable/DatePopover'
import _ from 'lodash'

export const createNewProject = (): NewProject => {
    return {
        projectId: generateId(),
        projectName: '',
        programName: '',
        projectNameError: '',
        selectedProgram: null,
        selectedProgramError: '',
        isActive: true,
        description: '',
    }
}

export const createNewProjectUnderProgram = (program) => {
    const proj = createNewProject()
    return {
        ...proj,
        selectedProgram: program,
    }
}

export const createNewServiceProject = (): NewServiceProject => {
    return {
        ...createNewProject(),
        serviceId: '',
        serviceIdError: '',
    }
}

export const createNewServiceProjectUnderProgram = (program): NewServiceProject => {
    const proj = createNewServiceProject()
    return {
        ...proj,
        selectedProgram: program,
    }
}

export const findProjectIndex = (newProjects: (NewProject | NewServiceProject)[], id: string) => {
    return newProjects.findIndex((prj) => {
        return prj.projectId === id
    })
}

export const getNewProjectAdd = (
    newProjects: (NewProject | NewServiceProject)[],
    id: string,
    preSelectedProgram,
    projectType: ProjectType,
) => {
    const tempNewProjects = [...newProjects]
    const index = findProjectIndex(tempNewProjects, id)
    const isServiceProject = projectType === ProjectType.SERVICE_PROJECT
    tempNewProjects.forEach(function (prj) {
        if (!prj.projectName) {
            prj.projectNameError = isServiceProject
                ? SERVICE_SELECTION_VALIDATION
                : PROJECT_NAME_VALIDATION
        }
        if (!prj.selectedProgram?.value) {
            prj.selectedProgramError = PROGRAM_SELECT_VALIDATION
        }
    })

    if (isServiceProject) {
        const items = !preSelectedProgram.value
            ? createNewServiceProject()
            : createNewServiceProjectUnderProgram(preSelectedProgram)
        tempNewProjects.splice(index + 1, 0, items)
    } else {
        const items = !preSelectedProgram.value
            ? createNewProject()
            : createNewProjectUnderProgram(preSelectedProgram)
        tempNewProjects.splice(index + 1, 0, items)
    }
    return tempNewProjects
}

export const getNewProjectDelete = (
    newProjects: NewProject[] | NewServiceProject[],
    id: string,
) => {
    const tempNewProjects = [...newProjects]
    const index = findProjectIndex(tempNewProjects, id)
    tempNewProjects.splice(index, 1)
    return tempNewProjects
}

export const getNewProjectChange = (
    newProjects: NewProject[] | NewServiceProject[],
    id: string,
    type: string,
    value: any,
) => {
    const tempNewProjects = [...newProjects]
    const index = findProjectIndex(tempNewProjects, id)
    const projectIndex = index < 0 ? 0 : index
    tempNewProjects[projectIndex][type] = value
    return tempNewProjects
}

export interface NewProject {
    projectId: string
    projectName: string
    programName: string
    projectNameError: string
    selectedProgram: SelectProps.Option | null
    selectedProgramError: string
    isActive: boolean
    description: string
}

export interface NewServiceProject extends NewProject {
    serviceId: string
    serviceIdError: string
}

export const validateCreateInputs = (projects: (NewProject | NewServiceProject)[]) => {
    for (const proj of projects) {
        const isServiceProject = 'serviceId' in proj
        const invalidProjectCondition =
            !isServiceProject &&
            (proj.projectNameError ||
                proj.selectedProgramError ||
                !proj.projectName ||
                !proj.selectedProgram)
        const invalidServiceProjectCondition =
            isServiceProject &&
            (proj.serviceIdError ||
                proj.selectedProgramError ||
                !proj.serviceId ||
                !proj.selectedProgram)
        if (invalidProjectCondition || invalidServiceProjectCondition) {
            return false
        }
    }
    return true
}

export const createProjects = (apiClient, payload) => {
    return apiClient.post(`/projects/bulk-create`, JSON.stringify(payload))
}

export const getNewProjectPayload = (
    latestYear: string,
    projects: (NewProject | NewServiceProject)[],
    selectBusinessEntityId: string,
    preSelectedProgram: any = undefined,
    programMetadata: any = undefined,
) => {
    const projectPayload: any[] = []
    for (const project of projects) {
        const isServiceProject = 'serviceId' in project
        const invalidProjectPayload =
            !isServiceProject &&
            (!project.projectName || project.projectNameError || !project.selectedProgram?.value)
        const invalidServiceProjectPayload =
            isServiceProject &&
            (!project.serviceId || project.serviceIdError || !project.selectedProgram?.value)
        if (invalidProjectPayload || invalidServiceProjectPayload) {
            return []
        }
        const programIds = project.selectedProgram?.value
            ? project.selectedProgram.value.split(PROGRAM_ID_SEPARATOR)
            : ['', '']

        const payload = {
            business_entity_id: selectBusinessEntityId,
            program_id: programIds[0],
            local_program_id: programIds[1],
            project_id: project.projectId,
            program_project_year: latestYear,
            project_name: project.projectName.trim(),
            is_active: project.isActive ? 'True' : 'False',
            description: project.description,
            program_name: project.selectedProgram?.label,
        }
        projectPayload.push(
            isServiceProject ? { ...payload, service_id: project.serviceId.trim() } : payload,
        )
    }

    return projectPayload
}

export const projectListColumnDef = [
    {
        id: 'project_id',
        header: 'Project ID',
        cell: (item: any) => item.project_id,
    },
    {
        id: 'project_name',
        header: 'Project',
        sortingField: 'project_name',
        cell: (item: any) => item.project_name,
    },
    {
        id: 'program_project_year',
        header: 'Project Year',
        sortingField: 'program_project_year',
        cell: (item: any) => item.program_project_year,
    },
    {
        id: 'program_id',
        header: 'Program ID',
        sortingField: 'program_id',
        cell: (item: any) => item.program_id,
    },
    {
        id: 'local_program_id',
        header: 'Local (BE-Specific) Program ID',
        sortingField: 'local_program_id',
        cell: (item: any) => item.local_program_id,
    },
    {
        id: 'description',
        header: 'Description',
        cell: (item: any) => (
            <TextPopover text={item.description} lengthLimit={DESCRIPTION_LENGTH_LIMIT} />
        ),
        maxWidth: 250,
    },
    {
        id: 'is_active_for_year',
        header: 'Active',
        cell: (item: any) => getActiveStatus(item),
        sortingField: 'is_active_for_year',
    },
    {
        id: 'program_name',
        header: 'Program',
        sortingField: 'program_name',
        cell: (item: any) => item.program_name,
    },
    {
        id: 'created_by',
        header: 'Created by',
        cell: (item) => item.created_by,
        sortingField: 'created_by',
    },
    {
        id: 'updated_by',
        header: 'Updated by',
        cell: (item) => item.updated_by,
        sortingField: 'updated_by',
    },
    {
        id: 'created_at',
        header: 'Created at',
        cell: (item) => <DatePopover date={convertToLocalTime(item.created_at)} />,
        sortingField: 'created_at',
    },
    {
        id: 'updated_at',
        header: 'Updated at',
        cell: (item) => <DatePopover date={convertToLocalTime(item.updated_at)} />,
        sortingField: 'updated_at',
    },
]

const getActiveStatus = (item) => {
    if ('service_id' in item) {
        // TODO: revisit once active_for_year service active/inactive logic is implemented
        if (item.is_active === 'True') {
            return <StatusIndicator type={STATUS_INDICATOR_TYPES.SUCCESS}>Yes</StatusIndicator>
        } else {
            return <StatusIndicator type={STATUS_INDICATOR_TYPES.WARNING}>No</StatusIndicator>
        }
    } else {
        if (!('is_active_for_year' in item)) {
            return <StatusIndicator type={STATUS_INDICATOR_TYPES.SUCCESS}>Yes</StatusIndicator>
        } else if (item.is_active_for_year === 'True') {
            return <StatusIndicator type={STATUS_INDICATOR_TYPES.SUCCESS}>Yes</StatusIndicator>
        } else {
            return <StatusIndicator type={STATUS_INDICATOR_TYPES.WARNING}>No</StatusIndicator>
        }
    }
}
